import { useEffect } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';

export default function ServiceAreasPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags
    document.title = 'Service Areas | Evernorth AI | Northwest Georgia AI Consulting';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Evernorth AI serves businesses across Northwest Georgia including Rome, Calhoun, Dalton, Cartersville, Jasper, Ellijay, Ringgold, Kennesaw, and surrounding areas with AI consulting and training workshops.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/service-areas`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Consulting and Training Services",
      "description": "AI consulting, training workshops, and readiness audits for businesses in Northwest Georgia",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Evernorth AI",
        "telephone": "+1-706-331-5858"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/service-areas`,
      "areaServed": [
        {
          "@type": "City",
          "name": "Rome",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        },
        {
          "@type": "City",
          "name": "Calhoun",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        },
        {
          "@type": "City",
          "name": "Dalton",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        },
        {
          "@type": "City",
          "name": "Cartersville",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        },
        {
          "@type": "City",
          "name": "Jasper",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        },
        {
          "@type": "City",
          "name": "Ellijay",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        },
        {
          "@type": "City",
          "name": "Kennesaw",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        },
        {
          "@type": "City",
          "name": "Ringgold",
          "containedInPlace": {
            "@type": "State",
            "name": "Georgia"
          }
        }
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  const cities = [
    'Dalton', 'Calhoun', 'Rome', 'Cartersville', 'Jasper',
    'Ellijay', 'Ringgold', 'Kennesaw', 'Surrounding communities'
  ];

  const benefits = [
    {
      icon: 'ri-map-pin-user-line',
      title: 'Local Expertise',
      description: 'We understand the unique challenges and opportunities of Northwest Georgia businesses.'
    },
    {
      icon: 'ri-presentation-line',
      title: 'Virtual & On-Site Training',
      description: 'We serve you virtually or we come to your location for hands-on workshops and personalized training sessions.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Regional Support',
      description: 'Quick response times and local support for all your AI implementation needs.'
    }
  ];

  return (
    <PageLayout>
      <PageHero
        badge="Where We Serve"
        title="Proudly Serving"
        titleHighlight="Northwest Georgia"
        description="We're dedicated to bringing world-class AI training and consulting to small town businesses throughout the Northwest Georgia region"
      />

      <section className="py-24 bg-[#f8f8f9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-navy">
              Serving <span className="text-gold">Northwest Georgia</span>
            </h2>
            <p className="text-lg text-charcoal max-w-3xl mx-auto leading-relaxed">
              Evernorth AI works with organizations throughout Northwest Georgia because serving local matters. Working locally allows us to understand regional industries, meet in person when needed, and support teams consistently.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
            <div className="bg-white p-8 rounded-2xl border border-soft-gray/30 shadow-sm">
              <h3 className="text-2xl font-bold mb-6 text-navy">We Primarily Serve</h3>
              <div className="grid grid-cols-2 gap-4">
                {cities.map((city, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-charcoal hover:text-gold transition-colors"
                  >
                    <i className="ri-map-pin-2-fill text-gold text-sm"></i>
                    <span className="text-sm font-medium">{city}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-8 border-t border-soft-gray/30">
                <p className="text-sm text-charcoal mb-4">
                  <strong className="text-navy">Limited availability</strong> is offered in Chattanooga and North Atlanta suburbs.
                </p>
              </div>
            </div>

            <div className="relative h-[500px] w-full rounded-2xl overflow-hidden shadow-sm">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d207000!2d-84.9499!3d34.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x885f5b1b1b1b1b1b%3A0x0!2sCalhoun%2C%20GA%2030701!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-2xl"
                title="Northwest Georgia Service Area Map"
              ></iframe>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl border border-soft-gray/30 text-center hover:border-gold/50 transition-all shadow-sm"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-navy/10 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-navy`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-navy">{benefit.title}</h3>
                <p className="text-charcoal leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="inline-block bg-gradient-to-r from-navy to-[#062299] p-8 rounded-2xl shadow-2xl max-w-3xl">
              <h3 className="text-2xl font-bold mb-4 text-white">Ready to Get Started?</h3>
              <p className="text-white/90 mb-6">
                Schedule a free consultation to discuss how AI can transform your business.
              </p>
              <a
                href="https://calendly.com/evernorthai/discovery"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
              >
                Book Free Consultation
              </a>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
